/**
 * Asking the reader what a supplier's quotation says.
 *
 * Returns the lines exactly as the model produced them. Working out which
 * catalogue product each one is, is `planImport` in packages/core (rule 5), and
 * a person's before any of it is saved.
 */

import {
  supplierMatchBatches,
  type QuotedLine,
  type SupplierMatch,
} from "@roof-hawk/core";

import { fileAsBase64 } from "./crewDocuments";
import { supabase } from "./supabase";

export interface ReadQuote {
  supplier: string | null;
  quotedOn: string | null;
  expiresOn: string | null;
  /**
   * The subtotal and the sales tax printed on the page, when it printed them.
   *
   * Null is "the page did not say" and never "there was none" — which is why
   * the reader is told not to work a tax figure out for itself. A receipt read
   * on a material order is compared against the order's own tax rate, and a
   * computed zero there would read as a supplier who charges no tax.
   */
  printedSubtotal: number | null;
  printedTax: number | null;
  /**
   * The total printed on the page — never computed here.
   *
   * It exists so the review screen can put it beside the sum of the lines. If
   * the two disagree, the reader missed a line or misread a number, and that
   * is the one check a person can make in a second against the paper.
   */
  printedTotal: number | null;
  lines: QuotedLine[];
  /** Anything the reader was unsure of, in its own words. */
  notes: string | null;
}

export async function readSupplierQuote(file: File): Promise<ReadQuote> {
  const { data, error } = await supabase.functions.invoke(
    "read-supplier-quote",
    {
      body: {
        file: {
          media_type: file.type || "application/pdf",
          data: await fileAsBase64(file),
          filename: file.name,
        },
      },
    },
  );

  if (error) {
    // The function reports its own trouble in the body — "the reader is busy",
    // "not configured", "that quote was too long" — and any of those is far
    // more use than "Edge Function returned a non-2xx status code".
    const detail = await readFunctionError(error);
    throw new Error(detail ?? error.message);
  }

  const payload = data as { quote?: Record<string, unknown> } | null;
  if (!payload?.quote)
    throw new Error("The quote reader gave an answer we could not read.");

  return shapeQuote(payload.quote);
}

/**
 * The model's answer, in the shape the rest of the app uses.
 *
 * Defensive about every field. The schema is strict so this should not be
 * necessary, but the alternative to a guard here is a crash on a screen
 * somebody has just uploaded a file to.
 */
export function shapeQuote(quote: Record<string, unknown>): ReadQuote {
  const rawItems = Array.isArray(quote.items) ? quote.items : [];
  return {
    supplier: text(quote.supplier),
    quotedOn: text(quote.quoted_on),
    expiresOn: text(quote.expires_on),
    printedSubtotal: number(quote.printed_subtotal),
    printedTax: number(quote.printed_tax),
    printedTotal: number(quote.printed_total),
    notes: text(quote.notes),
    lines: rawItems
      .map((raw) => {
        const item = (raw ?? {}) as Record<string, unknown>;
        return {
          description: text(item.description) ?? "",
          qty: number(item.qty) ?? 0,
          unit: text(item.unit),
          unitPrice: number(item.unit_price) ?? 0,
        };
      })
      // A line with no description is nothing anybody can act on — there is no
      // product to match and nothing to type into the name box.
      .filter((line) => line.description.trim() !== ""),
  };
}

const text = (value: unknown): string | null =>
  typeof value === "string" && value.trim() !== "" ? value.trim() : null;

const number = (value: unknown): number | null => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

/** The sentence the function put in the body, when there is one. */
async function readFunctionError(error: unknown): Promise<string | null> {
  const context = (error as { context?: unknown })?.context;
  if (!(context instanceof Response)) return null;
  try {
    const body = (await context.clone().json()) as { error?: unknown };
    return typeof body?.error === "string" ? body.error : null;
  } catch {
    return null;
  }
}

/**
 * Ask Jev which of our lines each supplier line is, among the options code
 * shortlisted (ADR 0155). `orderId` set: the options are that order's lines, for
 * a receipt. Absent: catalogue products, for a quote.
 *
 * Never sends what the function would refuse: `supplierMatchBatches` leaves
 * out lines with nothing to ask, cuts each line to the function's caps and
 * splits a long document into several requests, whose answers come back
 * merged, each under its own line's key.
 *
 * Throws the function's own sentence. Every caller treats a failure as "no
 * help from Jev this time" and carries on with word overlap.
 */
export async function matchSupplierLines(body: {
  orderId?: string | null;
  lines: { key: string; description: string; unit?: string | null }[];
  options: Record<string, readonly string[]>;
}): Promise<SupplierMatch[]> {
  const batches = supplierMatchBatches(body.lines, body.options);
  const answers = await Promise.all(
    batches.map((batch) =>
      askQuoteMatch({
        mode: "supplier_lines",
        orderId: body.orderId ?? null,
        lines: batch.lines,
        options: batch.options,
      }),
    ),
  );
  return answers.flat();
}

async function askQuoteMatch(
  body: Record<string, unknown>,
): Promise<SupplierMatch[]> {
  const { data, error } = await supabase.functions.invoke("quote-match", {
    body,
  });
  if (error) {
    let detail: string | null = null;
    try {
      const context = (error as { context?: Response }).context;
      detail = context
        ? (((await context.json()) as { error?: string }).error ?? null)
        : null;
    } catch {
      // Nothing better than the generic message.
    }
    throw new Error(detail ?? error.message);
  }
  const matches = (data as { matches?: SupplierMatch[] } | null)?.matches;
  if (!Array.isArray(matches)) {
    throw new Error("Jev gave an answer we could not read.");
  }
  return matches;
}
